import { useHistory } from "react-router";
import { FlightError } from "@b2bportal/air-booking-api";
import { type CardError, CardErrorEnum } from "@b2bportal/card-api";
import { Funnel } from "@b2bportal/core-types";
import {
  CoreFlightSteps,
  ensureExhaustive,
  NavigationFlow,
} from "@b2bportal/core-utilities";
import { type GroundError, GroundErrorEnum } from "@b2bportal/ground-api";
import { type LodgingError, LodgingErrorEnum } from "@b2bportal/lodging-api";
import {
  type RewardsPaymentError,
  RewardsPaymentErrorEnum,
} from "@b2bportal/lotte-payment-api";
import { type KcpError, KcpErrorEnum } from "@b2bportal/payment-methods-api";
import type { PriceFreezeError } from "@b2bportal/price-freeze-api";
import {
  type ErrorCode,
  Payment,
  type PaymentError,
  Product,
  type ProductError,
  type PurchaseError,
  PurchaseErrorEnum,
} from "@b2bportal/purchase-api";
import { useCheckoutSend } from "@checkout/context";
import {
  type CartFulfillError,
  CartFulfillErrorModal,
  cartFulfillSelectors,
  CartFulfillState,
  CartQuoteEventType,
  cartQuoteSelectors,
  Event,
  getChildState,
  getParentState,
  ParentState,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { logger } from "@hopper-b2b/datadog";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { EventObject } from "xstate";
import type { ErrorContentProps, ErrorModalProps } from "../../ErrorModal";
import { getAirPriceFreezeError } from "../utilities";
import { useGoBackToCarSearch } from "./useGoBackToCarSearch";
import { useGoBackToFlightShop } from "./useGoBackToFlightShop";
import { useGoBackToHotelSearch } from "./useGoBackToHotelSearch";

export interface CartFulfillErrorModalProps extends Partial<ErrorModalProps> {
  // Deprecate the funnel property
  // @deprecated pass searchAgain function instead
  funnel?: Funnel;
  cartError?: CartFulfillError;
  useOldNavigation?: boolean;
  searchAgain?: () => void;
}

export const useCartFulfillErrorModal = ({
  funnel,
  cartError,
  useOldNavigation = false,
  searchAgain: searchAgainFromProps,
}: CartFulfillErrorModalProps) => {
  const { t } = useI18nContext();
  const history = useHistory();
  const send = useCheckoutSend<EventObject>();
  const goToHotelSearch = useGoBackToHotelSearch();
  const goToCarSearch = useGoBackToCarSearch();
  const cartErrorModal = useSelector(cartFulfillSelectors.getCartFulfillError);
  const clearError = () => send(Event.CLEAR_SCHEDULE_FULFILL_ERROR);
  const goToStep = NavigationFlow.useGoToStep();
  const goBackToFlightShop = useGoBackToFlightShop();

  const fulfillError = cartError || cartErrorModal;

  const [state] = useCheckoutState();
  const open =
    (getParentState(state.value) === ParentState.cartFulfill ||
      getParentState(state.value) === ParentState.pointsAuth) &&
    getChildState(state.value) === CartFulfillState.error;

  const searchAgain = (funnel?: Funnel) => {
    clearError();
    if (searchAgainFromProps != null) {
      searchAgainFromProps();
      return;
    }
    if (funnel == null) {
      throw new Error("Specify either funnel or searchAgain in props");
    }

    // Deprecate the funnel property in props and remove this switch
    // instead "searchAgain" should be passed to the props
    switch (funnel) {
      case Funnel.Air:
        if (useOldNavigation) {
          goBackToFlightShop();
        } else {
          goToStep(CoreFlightSteps.DEPARTURE, true, { refetchData: true });
        }
        break;
      case Funnel.Lodging:
        goToHotelSearch();
        break;
      case Funnel.Ground:
        goToCarSearch();
        break;
      case Funnel.Package:
        // TODO once APAC adds Package funnel, we need to revisit here
        // For now we default to hotels search
        goToHotelSearch();
        break;
      case Funnel.Activities:
        // TODO once APAC adds Activities funnel, we need to revisit here
        // For now we default to hotels search
        goToHotelSearch();
        break;
      case Funnel.Trips:
        // TODO once APAC adds Trips funnel, we need to revisit here
        // For now we default to hotels search
        goToHotelSearch();
        break;
      default:
        ensureExhaustive(funnel);
    }
  };

  const updateFlightTravelers = () => {
    clearError();
    send(Event.OPEN_PASSENGER_PICKER);
  };

  const updateTraveler = () => {
    clearError();
    send(Event.GO_TO_TRAVELER_SELECT);
  };

  // Note that once fulfillment is scheduled (fail or success), we can't go to payment screen directly and have to quote again
  const updatePayment = () => {
    clearError();
    send(Event.GO_TO_PAYMENT);
  };

  const goToConfirmation = () => {
    clearError();
    send(Event.GO_TO_BOOKING_CONFIRMATION);
  };

  const allowedQuoteRetry = useSelector(cartQuoteSelectors.getAllowQuoteRetry);
  const retryQuote = () => {
    send(CartQuoteEventType.RETRY_QUOTE);
    history.replace(history.location);
  };
  const getRetryQuoteOrSearchAgain = (funnel?: Funnel) => {
    const getFindNewText = (funnel?: Funnel) => {
      switch (funnel) {
        case Funnel.Air:
          return t("checkoutError.findNewFlight");
        case Funnel.Ground:
          return t("checkoutError.findNewCar");
        case Funnel.Lodging:
          return t("checkoutError.findNewHotel");
        default:
          return t("checkoutError.searchAgain");
      }
    };
    return allowedQuoteRetry
      ? {
          primaryButtonText: t("checkoutError.tryAgain"),
          primaryOnClick: retryQuote,
          secondaryButtonText: getFindNewText(funnel),
          secondaryOnClick: () => searchAgain(funnel),
        }
      : {
          primaryButtonText: getFindNewText(funnel),
          primaryOnClick: () => searchAgain(funnel),
        };
  };
  const DEFAULT_ERROR_MODEL_PROPS: ErrorContentProps = {
    primaryButtonText: t("checkoutError.tryAgain"),
    primaryOnClick: allowedQuoteRetry ? retryQuote : () => searchAgain(funnel),
    title: t("checkoutError.genericErrorTitle"),
    subtitle: t("checkoutError.genericErrorSubtitle"),
  };
  const DEFAULT_FLIGHT_ERROR_MODEL_PROPS: ErrorContentProps = {
    ...DEFAULT_ERROR_MODEL_PROPS,
    ...getRetryQuoteOrSearchAgain(Funnel.Air),
  };

  const DEFAULT_HOTEL_ERROR_MODEL_PROPS: ErrorContentProps = {
    ...DEFAULT_ERROR_MODEL_PROPS,
    ...getRetryQuoteOrSearchAgain(Funnel.Lodging),
  };

  const DEFAULT_GROUND_ERROR_MODEL_PROPS: ErrorContentProps = {
    ...DEFAULT_ERROR_MODEL_PROPS,
    ...getRetryQuoteOrSearchAgain(Funnel.Ground),
  };

  const getHotelError = (hotelError: LodgingError): ErrorContentProps => {
    switch (hotelError.LodgingError) {
      case LodgingErrorEnum.CheckInMinimumAgeNotMet:
        return {
          title: t("checkoutError.hotelCheckInMinimumAgeNotMetTitle"),
          subtitle: t("checkoutError.hotelCheckInMinimumAgeNotMetSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTraveler,
        };
      case LodgingErrorEnum.Unavailable:
      case LodgingErrorEnum.LackOfInventory:
        return {
          title: t("checkoutError.hotelLackOfInventoryTitle"),
          subtitle: t("checkoutError.hotelLackOfInventorySubtitle"),
          ...getRetryQuoteOrSearchAgain(Funnel.Lodging),
        };
      case LodgingErrorEnum.RateNotAvailable:
        return {
          title: t("checkoutError.hotelRateNotAvailableTitle"),
          subtitle: t("checkoutError.hotelRateNotAvailableSubtitle"),
          ...getRetryQuoteOrSearchAgain(Funnel.Lodging),
        };
      default:
        return DEFAULT_HOTEL_ERROR_MODEL_PROPS;
    }
  };

  const getCarError = (carError: GroundError): ErrorContentProps => {
    switch (carError.GroundError) {
      case GroundErrorEnum.DuplicateBooking:
        return {
          title: t("checkoutError.duplicateBookingTitle"),
          subtitle: t("checkoutError.duplicateBookingSubtitle"),
          primaryButtonText: t("checkoutError.acknowlegement"),
          primaryOnClick: goToConfirmation,
        };
      case GroundErrorEnum.InvalidLoyaltyNumber:
        return {
          title: t("checkoutError.invalidLoyaltyNumberTitle"),
          subtitle: t("checkoutError.invalidLoyaltyNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTraveler,
        };
      case GroundErrorEnum.AgeRequirement:
        return {
          title: t("checkoutError.driverAgeNotMetTitle"),
          subtitle: t("checkoutError.driverAgeNotMetSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateTraveler,
        };
      case GroundErrorEnum.RateUnavailable:
        return {
          title: t("checkoutError.carRateNotAvailableTitle"),
          subtitle: t("checkoutError.carRateNotAvailableSubtitle"),
          ...getRetryQuoteOrSearchAgain(Funnel.Ground),
        };
      default:
        return DEFAULT_GROUND_ERROR_MODEL_PROPS;
    }
  };

  const getFlightError = (flightError: FlightError): ErrorContentProps => {
    switch (flightError) {
      case FlightError.CheckInMinimumAgeNotMet:
        return {
          title: t("checkoutError.checkInMinimumAgeNotMetTitle"),
          subtitle: t("checkoutError.checkInMinimumAgeNotMetSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.DuplicateBooking:
        return {
          title: t("checkoutError.duplicateBookingTitle"),
          subtitle: t("checkoutError.duplicateBookingSubtitle"),
          primaryButtonText: t("checkoutError.acknowlegement"),
          primaryOnClick: goToConfirmation,
        };
      case FlightError.ExpiredPassport:
        return {
          title: t("checkoutError.expiredPassportTitle"),
          subtitle: t("checkoutError.expiredPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.IllegalLapInfantKTN:
        return {
          title: t("checkoutError.illegalLapInfantKTNTitle"),
          subtitle: t("checkoutError.illegalLapInfantKTNSubtitle"), // todo: we don't have the pax name
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.IllegalLapInfantRedress:
        return {
          title: t("checkoutError.illegalLapInfantRedressTitle"),
          subtitle: t("checkoutError.illegalLapInfantRedressSubtitle"), // todo: we don't have the pax name
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.Inactivity:
        // inactivity here means that the flight booking session timed out
        return {
          title: t("checkoutError.flightsInActivityTitle"),
          subtitle: t("checkoutError.flightsInActivitySubtitle"),
          ...getRetryQuoteOrSearchAgain(Funnel.Air),
        };
      case FlightError.InvalidCustomer:
        return {
          title: t("checkoutError.invalidCustomerTitle"),
          subtitle: t("checkoutError.invalidCustomerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.InvalidPassengers:
        return {
          title: t("checkoutError.invalidPassenger"),
          subtitle: t("checkoutError.invalidPassengerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.InvalidPassportNumber:
        return {
          title: t("checkoutError.invalidPassportNumberTitle"),
          subtitle: t("checkoutError.invalidPassportNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.LapInfantTooOld:
        return {
          title: t("checkoutError.lapInfantTooOldTitle"),
          subtitle: t("checkoutError.lapInfantTooOldSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.LapInfantsUnsupported:
        return {
          title: t("checkoutError.lapInfantsUnsupportedTitle"),
          subtitle: t("checkoutError.lapInfantsUnsupportedSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.MalformedKnownTravelerNumber:
        return {
          title: t("checkoutError.malformedKnownTravelerNumberTitle"),
          subtitle: t("checkoutError.malformedKnownTravelerNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.MalformedRedressNumber:
        return {
          title: t("checkoutError.malformedRedressNumberTitle"),
          subtitle: t("checkoutError.malformedRedressNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.MissingAirlineLocator:
        return {
          title: t("checkoutError.missingAirlineLocatorTitle"),
          subtitle: t("checkoutError.missingAirlineLocatorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: () => searchAgain(Funnel.Air),
        };
      case FlightError.MissingNationality:
        return {
          title: t("checkoutError.missingNationalityTitle"),
          subtitle: t("checkoutError.missingNationalitySubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: () => searchAgain(Funnel.Air),
        };
      case FlightError.MissingPassport:
        return {
          title: t("checkoutError.missingPassportTitle"),
          subtitle: t("checkoutError.missingPassportSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.MissingPassportNumber:
        return {
          title: t("checkoutError.missingPassportNumberTitle"),
          subtitle: t("checkoutError.missingPassportNumberSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.NoAdultPassenger:
        return {
          title: t("checkoutError.noAdultPassengerTitle"),
          subtitle: t("checkoutError.noAdultPassengerSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.NoContactInformation:
        return {
          title: t("checkoutError.noContactInformationTitle"),
          subtitle: t("checkoutError.noContactInformationSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"), // todo: update contact info
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.NoTicketlessResponse:
        return {
          title: t("checkoutError.noTicketlessResponseTitle"),
          subtitle: t("checkoutError.noTicketlessResponseSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: () => searchAgain(Funnel.Air),
        };
      case FlightError.ProviderError:
        return {
          title: t("checkoutError.providerErrorTitle"),
          subtitle: t("checkoutError.providerErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: () => searchAgain(Funnel.Air),
        };
      case FlightError.SeatedInfantsUnsupported:
        return {
          title: t("checkoutError.seatedInfantsUnsupportedTitle"),
          subtitle: t("checkoutError.seatedInfantsUnsupportedSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.TicketingNotStarted:
        return {
          title: t("checkoutError.ticketingNotStartedTitle"),
          subtitle: t("checkoutError.ticketingNotStartedSubtitle"),
          primaryButtonText: "", // todo close CTA
          primaryOnClick: goToConfirmation,
        };
      case FlightError.TicketingUnconfirmed:
        return {
          title: t("checkoutError.ticketingUnconfirmedTitle"),
          subtitle: t("checkoutError.ticketingUnconfirmedSubtitle"),
          primaryButtonText: "", // todo go to confirmation page
          primaryOnClick: goToConfirmation,
        };
      case FlightError.TicketingUnknown:
        return {
          title: t("checkoutError.ticketingUnknownTitle"),
          subtitle: t("checkoutError.ticketingUnknownSubtitle"),
          primaryButtonText: "", // todo go to confirmation page
          primaryOnClick: goToConfirmation,
        };
      case FlightError.TooManyLapInfants:
        return {
          title: t("checkoutError.tooManyLapInfantsTitle"),
          subtitle: t("checkoutError.tooManyLapInfantsSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.TooManyPassengers:
        return {
          title: t("checkoutError.tooManyPassengersTitle"),
          subtitle: t("checkoutError.tooManyPassengersSubtitle"),
          primaryButtonText: t("checkoutError.updateTraveler"),
          primaryOnClick: updateFlightTravelers,
        };
      case FlightError.TransientServiceError:
        return {
          title: t("checkoutError.transientServiceErrorTitle"),
          subtitle: t("checkoutError.transientServiceErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: () => searchAgain(Funnel.Air),
        };
      case FlightError.UnknownSabreAppError:
        return {
          title: t("checkoutError.unknownSabreAppErrorTitle"),
          subtitle: t("checkoutError.unknownSabreAppErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: () => searchAgain(Funnel.Air),
        };
      case FlightError.UpgradeRequired:
        return {
          title: t("checkoutError.upgradeRequiredTitle"),
          subtitle: t("checkoutError.unknownSabreAppErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: () => searchAgain(Funnel.Air),
        };
      default:
        // FlightError.Unknown
        return DEFAULT_FLIGHT_ERROR_MODEL_PROPS;
    }
  };

  const getProductError = (error: ProductError): ErrorContentProps => {
    switch (error.value.type) {
      case Product.AirPriceFreeze:
        return getAirPriceFreezeError(
          error.value.value as PriceFreezeError,
          DEFAULT_ERROR_MODEL_PROPS,
          noAvailabilityProps
        );
      case Product.Flight:
      case Product.Seats:
        // https://github.com/hopper-org/b2b-interfaces/blob/8316205c08da3b4405647ff5245f3bdc76f0ef73/purchase/src/main/scala/com/hopper/b2b/purchase/Models.scala#L114
        return getFlightError(error.value.value as FlightError);
      case Product.Hotel:
        return getHotelError(error.value.value as LodgingError);
      case Product.Ground:
        return getCarError(error.value.value as GroundError);
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };

  const getCardPaymentError = ({
    CardError: errorType,
  }: CardError): ErrorContentProps => {
    const sharedProps = {
      title: t("checkoutError.genericErrorTitle"),
      primaryButtonText: t("checkoutError.tryAgain"),
      hideSecondaryButton: true,
      primaryOnClick: retryQuote,
      onClose: clearError,
    };

    // More Errors can be mapped to specific strings in future
    switch (errorType) {
      case CardErrorEnum.NotEnoughBalance:
      case CardErrorEnum.LimitExceeded:
        return {
          ...sharedProps,
          subtitle: t("checkoutError.notEnoughBalance"),
        };
      default:
        return {
          ...sharedProps,
          subtitle: t("checkoutError.genericCardError"),
        };
    }
  };

  const getRewardPaymentError = (error: PaymentError): ErrorContentProps => {
    if (error.value.value?.RewardsPaymentError === "AuthorizeFailed") {
      return {
        title: t("checkoutError.rewardsPaymentFailedTitle"),
        subtitle: t("checkoutError.rewardsPaymentFailedSubtitle"),
        primaryButtonText: t("checkoutError.updatePayment"),
        primaryOnClick: updatePayment,
        onClose: clearError,
        secondaryButtonText: t("checkoutError.tryAgain"),
        secondaryOnClick: retryQuote,
      };
    } else if (error.value.value?.RewardsPaymentError === "PointsAuthFailure") {
      return {
        title: t("checkoutError.pointsAuthFailureTitle"),
        subtitle: t("checkoutError.pointsAuthFailureSubtitle"),
        primaryButtonText: t("checkoutError.updatePayment"),
        // Points auth failure happens before fulfillment schedule in Lotte, so we can go to payment screen directly to retry
        primaryOnClick: updatePayment,
        onClose: clearError,
      };
    } else if (error.value.value?.RewardsPaymentError === "CaptureFailure") {
      return {
        title: t("checkoutError.captureFailureTitle"),
        subtitle: t("checkoutError.captureFailureSubtitle"),
        ...getRetryQuoteOrSearchAgain(funnel),
      };
    } else {
      return {
        title: t("checkoutError.rewardsPaymentFailedTitle"),
        subtitle: t("checkoutError.rewardsPaymentFailedSubtitle"),
        primaryButtonText: t("checkoutError.tryAgain"),
        // Got to retry quote first unless known it is happening before fulfillment schedule
        primaryOnClick: retryQuote,
        // Ensure loop existing option if quote keep failing
        secondaryButtonText: t("checkoutError.searchAgain"),
        secondaryOnClick: () => searchAgain(funnel),
        onClose: clearError,
      };
    }
  };

  const getKcpError = (error: PaymentError): ErrorContentProps => {
    // TODO: We are not differentiating kcp errors at the moment
    return {
      title: t("kcpCheckoutError.genericErrorTitle"),
      subtitle: t("kcpCheckoutError.genericErrorSubtitle"),
      ...getRetryQuoteOrSearchAgain(funnel),
    };
  };

  const noAvailabilityProps: ErrorContentProps = {
    title: t("checkoutError.noAvailabilityTitle"),
    subtitle: t("checkoutError.noAvailabilitySubtitle"),
    primaryButtonText: t("checkoutError.searchAgain"),
    primaryOnClick: () => searchAgain(Funnel.Air),
  };

  const getErrorCode = (error: ErrorCode): ErrorContentProps => {
    switch (error.cause) {
      case "InMaintenance":
        return {
          title: t("checkoutError.maintenanceTitle"),
          subtitle: t("checkoutError.maintenanceSubtitle"),
          primaryButtonText: t("checkoutError.updatePayment"),
          primaryOnClick: () => updatePayment(),
        };
      default:
        return {
          title: DEFAULT_ERROR_MODEL_PROPS.title,
          subtitle: DEFAULT_ERROR_MODEL_PROPS.subtitle,
          primaryButtonText: DEFAULT_ERROR_MODEL_PROPS.primaryButtonText,
          primaryOnClick: DEFAULT_ERROR_MODEL_PROPS.primaryOnClick,
        };
    }
  };

  const getPaymentError = (error: PaymentError): ErrorContentProps => {
    switch (error.value.type) {
      case Payment.AirPriceFreezeExerciseDiscount:
        return getAirPriceFreezeError(
          error.value.value as PriceFreezeError,
          DEFAULT_ERROR_MODEL_PROPS,
          noAvailabilityProps
        );
      case Payment.Card:
        return getCardPaymentError(error.value.value as CardError);
      case Payment.Rewards:
        return getRewardPaymentError(error);
      case Payment.KCP:
        return getKcpError(error);
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };

  const getErrorModalProps = (
    type?: CartFulfillErrorModal,
    data?: PurchaseError[] | KcpError[] | RewardsPaymentError[]
  ) => {
    logger().error("Cart fulfillment error:", { type, error: data?.[0] });
    switch (type) {
      case CartFulfillErrorModal.FailedPolling: {
        const head = data?.[0] as PurchaseError; // todo: we probably want to have some sort of priority order on errors?
        switch (head?.Error) {
          case PurchaseErrorEnum.ErrorCode:
            return getErrorCode(head as ErrorCode);
          case PurchaseErrorEnum.NoAvailability:
            return noAvailabilityProps;
          case PurchaseErrorEnum.PaymentError:
            return getPaymentError(head as PaymentError);
          case PurchaseErrorEnum.ProductError: // todo. Missing `Error` attribute
            return getProductError(head as ProductError);
          case PurchaseErrorEnum.FraudAutoReject:
            return {
              title: t("checkoutError.noAvailabilityTitle"),
              subtitle: t("checkoutError.noAvailabilitySubtitle"),
              primaryButtonText: t("checkoutError.searchAgain"),
              primaryOnClick: () => searchAgain(funnel),
            };
          case PurchaseErrorEnum.ExpiredFulfillToken:
          case PurchaseErrorEnum.InActivity:
          case PurchaseErrorEnum.InvalidSession:
          case PurchaseErrorEnum.ExpiredQuoteToken:
            return {
              title: t("checkoutError.inactivityTitle"),
              subtitle: t("checkoutError.inActivitySubtitle"),
              primaryButtonText: t("checkoutError.tryAgain"),
              primaryOnClick: retryQuote,
            };
          default:
            // - PurchaseErrorEnum.AuthError
            // - PurchaseErrorEnum.NoPaymentsProvided
            // - PurchaseErrorEnum.NonDistinctPayments
            return DEFAULT_ERROR_MODEL_PROPS;
        }
      }
      case CartFulfillErrorModal.KcpFailure: {
        // cases where we have kcp errors
        const head = data?.[0] as KcpError; // todo: we probably want to have some sort of priority order on errors?
        switch (head?.KcpError) {
          case KcpErrorEnum.InvalidAmount:
            return {
              title: t("kcpCheckoutError.invalidAmountTitle"),
              subtitle: t("kcpCheckoutError.invalidAmountSubtitle"),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          case KcpErrorEnum.InvalidParameter:
            return {
              title: t("kcpCheckoutError.invalidParameterTitle"),
              subtitle: t("kcpCheckoutError.invalidParameterSubtitle"),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          case KcpErrorEnum.Refused:
            return {
              title: t("kcpCheckoutError.refusedTitle"),
              subtitle: t("kcpCheckoutError.refusedSubtitle"),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          case KcpErrorEnum.Cancelled:
            return {
              title: t("kcpCheckoutError.cancelledTitle"),
              subtitle: t("kcpCheckoutError.cancelledSubtitle"),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          case KcpErrorEnum.UnexpectedResponse:
          default:
            return {
              title: t("kcpCheckoutError.genericErrorTitle"),
              subtitle: t("kcpCheckoutError.genericErrorSubtitle"),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
        }
      }
      // TODO: differentiate between KCP failures and user closing the KCP page
      case CartFulfillErrorModal.RewardsPaymentFailure: {
        const head = data?.[0] as RewardsPaymentError; // todo: we probably want to have some sort of priority order on errors?
        // cases where we have rewards payment failure
        switch (head?.RewardsPaymentError) {
          case RewardsPaymentErrorEnum.AccountNotFound:
            return {
              title: t("rewardPaymentCheckoutError.accountNotFoundTitle"),
              subtitle: t("rewardPaymentCheckoutError.accountNotFoundSubtitle"),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          case RewardsPaymentErrorEnum.AuthorizeFailure:
            return {
              title: t("rewardPaymentCheckoutError.authorizeFailureTitle"),
              subtitle: t(
                "rewardPaymentCheckoutError.authorizeFailureSubtitle"
              ),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          case RewardsPaymentErrorEnum.CaptureFailure:
            return {
              title: t("rewardPaymentCheckoutError.captureFailureTitle"),
              subtitle: t("rewardPaymentCheckoutError.captureFailureSubtitle"),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          case RewardsPaymentErrorEnum.CreatePaymentFailure:
            return {
              title: t("rewardPaymentCheckoutError.createPaymentFailureTitle"),
              subtitle: t(
                "rewardPaymentCheckoutError.createPaymentFailureSubtitle"
              ),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          case RewardsPaymentErrorEnum.InsufficientBalance:
            return {
              title: t("rewardPaymentCheckoutError.insufficientBalanceTitle"),
              subtitle: t(
                "rewardPaymentCheckoutError.insufficientBalanceSubtitle"
              ),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          case RewardsPaymentErrorEnum.NegativePaymentAmount:
            return {
              title: t("rewardPaymentCheckoutError.negativePaymentAmountTitle"),
              subtitle: t(
                "rewardPaymentCheckoutError.negativePaymentAmountSubtitle"
              ),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          case RewardsPaymentErrorEnum.PaymentSessionFailure:
            return {
              title: t("rewardPaymentCheckoutError.paymentSessionFailureTitle"),
              subtitle: t(
                "rewardPaymentCheckoutError.paymentSessionFailureSubtitle"
              ),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          case RewardsPaymentErrorEnum.RefundFailure:
            return {
              title: t("rewardPaymentCheckoutError.refundFailureTitle"),
              subtitle: t("rewardPaymentCheckoutError.refundFailureSubtitle"),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          case RewardsPaymentErrorEnum.RewardsErrorCode:
            return {
              title: t("rewardPaymentCheckoutError.rewardsErrorCodeTitle"),
              subtitle: t(
                "rewardPaymentCheckoutError.rewardsErrorCodeSubtitle",
                { code: head?.code }
              ),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          case RewardsPaymentErrorEnum.UserNotFound:
            return {
              title: t("rewardPaymentCheckoutError.userNotFoundTitle"),
              subtitle: t("rewardPaymentCheckoutError.userNotFoundSubtitle"),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
          default:
            return {
              title: t("rewardPaymentCheckoutError.genericErrorTitle"),
              subtitle: t("rewardPaymentCheckoutError.genericErrorSubtitle"),
              ...getRetryQuoteOrSearchAgain(funnel),
            };
        }
      }
      default:
        return DEFAULT_ERROR_MODEL_PROPS;
    }
  };
  const errorModalProps = getErrorModalProps(
    fulfillError?.type,
    fulfillError?.data as PurchaseError[]
  );
  return {
    errorModalProps: {
      open,
      onClose: () => searchAgain(funnel),
      ...errorModalProps,
    },
  };
};
